import { LabelOutlined as LabelOutlinedIcon } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useState } from 'react';
import {
  components,
  ControlProps,
  CSSObjectWithLabel,
  MultiValue,
  StylesConfig,
} from 'react-select';
import CreatableSelect from 'react-select/creatable';

import { createTag } from '@/domain/tags/actions/create-tag.action.ts';
import { Tag } from '@/domain/tags/entities/tag.entity.ts';
import { selectTags } from '@/domain/tags/tags.selectors.ts';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store.ts';

type TagFormProps = {
  handleChange: (tags: Array<Tag>) => void;
  defaultValue: Array<Tag>;
};

type TagOption = {
  value: number;
  label: string;
};

function TagForm({ handleChange, defaultValue }: TagFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [value, setValue] = useState<MultiValue<TagOption>>(
    defaultValue.map((tag) => ({ value: tag.id, label: tag.name })),
  );
  const dispatch = useAppDispatch();
  const tags = useAppSelector(selectTags);

  const handleTagChange = (tags: MultiValue<TagOption>) => {
    handleChange(
      tags.map((tag) => {
        return {
          id: tag.value,
          name: tag.label,
        };
      }),
    );
    setValue(tags);
  };

  const handleCreate = async (inputValue: string) => {
    setIsLoading(true);
    const createdTag: Tag = await dispatch(createTag(inputValue)).unwrap();

    const newOptions = [
      ...value,
      { value: createdTag.id, label: createdTag.name },
    ];
    setIsLoading(false);
    handleTagChange(newOptions);
  };

  const Control = ({
    children,
    ...props
  }: ControlProps<TagOption, true>): React.JSX.Element => {
    return (
      <components.Control {...props}>
        <LabelOutlinedIcon />
        {children}
      </components.Control>
    );
  };

  const styles: StylesConfig<TagOption, true> = {
    control: (baseStyles: CSSObjectWithLabel) => ({
      ...baseStyles,
      backgroundColor: '#140505',
      padding: '8px 0 8px 10px',
      borderColor: '#4a3f3f',
      color: 'white',
      input: {
        color: 'white !important',
      },
      '&:hover': {
        borderColor: 'white !important',
      },
    }),
    multiValue: (baseStyles: CSSObjectWithLabel) => ({
      ...baseStyles,
      backgroundColor: 'transparent',
      border: '1px solid white',
      borderRadius: '5px',
    }),
    multiValueLabel: (baseStyles: CSSObjectWithLabel) => ({
      ...baseStyles,
      color: 'white',
    }),
    menu: (baseStyles: CSSObjectWithLabel) => ({
      ...baseStyles,
      backgroundColor: '#403535',
      zIndex: 3,
    }),
    option: () => ({
      userSelect: 'none',
      padding: '8px 12px',
      boxSizing: 'border-box',
      cursor: 'pointer',
      display: 'block',
      fontSize: 'inherit',
      width: '100%',
      '&:hover, &:active': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
    }),
  };

  return (
    <Box sx={{ marginBottom: '5px' }}>
      <CreatableSelect<TagOption, true>
        isClearable
        components={{ Control }}
        isDisabled={isLoading}
        isLoading={isLoading}
        onChange={(newValue: MultiValue<TagOption>) =>
          handleTagChange(newValue)
        }
        onCreateOption={handleCreate}
        value={value}
        options={tags?.map((tag) => ({ value: tag.id, label: tag.name })) || []}
        styles={styles}
        isMulti
        placeholder="Tags"
        noOptionsMessage={() => 'Aucune valeur'}
        formatCreateLabel={(tagName: string) => `Créer le tag: ${tagName}`}
      />
    </Box>
  );
}

export default TagForm;
